(ns minorodata.components.connections.recipes
  (:require ["@xyflow/react" :refer [ReactFlow Handle Position]]
            [applied-science.js-interop :as js-interop]
            [clojure.set]
            [clojure.string]
            [lucide-react :refer [AlertTriangle ChevronDown ChevronUp]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.spinner.core :as c.spinner]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [ui.button]
            [ui.dialog]
            [ui.fields]
            [ui.typography]))


(defn submit-recipes-form
  [form-state]
  (let [form-data (form-component/model->fields form-state)
        group-id  (:group form-data)
        recipes   (->> (dissoc form-data :group)
                       (filter second)
                       (reduce (fn [acc [k _]]
                                 (conj acc
                                       {:group-id  group-id
                                        :recipe-id (name k)}))
                               []))]
    (dispatch [:recipes.api/compile-recipes {:params recipes}])))

(defn load-recipes-transforms
  [recipes]
  (dispatch [:transformations.api/load-search-results
             {:query      ""
              :filters    {:type :transform
                           :tags (mapv #(str "recipe-id:" (:recipe/recipe-id %)) recipes)}
              :pagination {:items-per-page 50 :page 0}}]))


(def open-details-registry
  (reagent/atom {}))


(def dag-layouts
  (reagent/atom {}))


(def node-types
  #js {"extract"
       (fn [params]
         (reagent/as-element
          [:div {:class "relative border rounded-xs bg-white px-4 py-5 text-xs text-center"}
           [:div {:class "absolute left-[1px] top-[1px] bg-kleene-neutral-05 text-stone-600 px-1 pt-[3px] pb-[1px] rounded-tl text-[7px] leading-none"}
            "Extract"]
           [:div {:class "absolute left-[1px] bottom-[1px] bg-kleene-neutral-05 text-stone-600 px-1 pt-[3px] pb-[1px] rounded-bl text-[7px] leading-none"}
            (or (js-interop/get-in params [:data :source-title]) "Source")]
           [:p {:class "text-stone-600"} (js-interop/get-in params [:data :label])]
           [:> Handle {:type "source"
                       :position (js-interop/get Position :Right)
                       :id "a"
                       :style {:top "50%"}}]]))

       "transform"
       (fn [params]
         (reagent/as-element
          [:div {:class "relative border rounded-xs bg-white p-4 pt-5 text-xs text-center"}
           [:> Handle {:type "target"
                       :position (js-interop/get Position :Left)
                       :id "a"
                       :style {:top "50%"}}]
           [:div {:class "absolute left-[1px] top-[1px] bg-kleene-neutral-05 text-stone-600 px-1 pt-[3px] pb-[1px] rounded-tl text-[7px] leading-none"}
            "Transform"]
           [:p {:class "text-stone-600"} (js-interop/get-in params [:data :label])]
           [:> Handle {:type "source"
                       :position (js-interop/get Position :Right)
                       :id "b"
                       :style {:top "50%"}}]]))

       "final-tarnsform"
       (fn [params]
         (reagent/as-element
          [:div {:class "relative border rounded-xs bg-white p-4 pt-5 text-xs text-center"}
           [:> Handle {:type "target"
                       :position (js-interop/get Position :Left)
                       :id "a"
                       :style {:top "50%"}}]
           [:div {:class "absolute left-[1px] top-[1px] bg-kleene-neutral-05 text-stone-600 px-1 pt-[3px] pb-[1px] rounded-tl text-[7px] leading-none"}
            "Transform"]
           [:p {:class "text-stone-600"} (js-interop/get-in params [:data :label])]]))})

(defn recipe-checkbox
  [{:keys [recipe on-close search searching? fields errors source]}]
  (let [{:recipe/keys [recipe-id deployable? name description templates]} recipe

        more-info-button-enabled? @(re-posh/subscribe [:unleash/is-enabled "recipes-dag-button"])

        open? (get @open-details-registry recipe-id)
        toggle-open #(swap! open-details-registry assoc recipe-id (not open?))

        recipe-tag (str "recipe-id:" recipe-id)
        created?  (boolean (some #(get (->> (:tags %)
                                            (mapv :transform-tag/value)
                                            set)
                                       recipe-tag) search))



        recipe-id        (:recipe/recipe-id recipe)
        source-id        (:source/source-id source)
        fetch-details    #(dispatch [:recipes.api/fetch-recipe-details
                                     recipe-id
                                     [:recipes.events/update-mini-dag-layout recipe-id source-id]])


        details-loading? @(re-posh/subscribe [:mold-http/loading? (str "recipe-details-request-" recipe-id)])

        dag @(re-posh/subscribe [:recipes.subs/mini-dag recipe-id source-id])

        disabled? (or searching? (not deployable?))

        checkbox-disabled? (or (boolean disabled?) created?)

        report-types  (->> recipe :recipe/source-report-platform-type
                           (filter identity)
                           (map #(second (clojure.string/split % #",")))
                           set)]

    [:div {:class "relative overflow-hidden px-5 py-2 my-2 border-b last:border-b-0 border-b-slate-200 
                  dark:border-b-0 dark:border-kleene-border rounded-lg bg-kleene-neutral-05 
                  dark:bg-kleene-dark-02 ease-in-out duration-300 transition-[height]"}
     [:div {:class "flex items-center"}
      [ui.fields/checkbox
       {:field       (get fields (keyword recipe-id))
        :errors      errors
        :id          recipe-id
        :type        "checkbox"
        :variant     "standard"
        :margin      "none"
        :color       "primary"
        :size        "small"
        :disabled    checkbox-disabled?
        :class       "mr-2 mt-0]"}]
      [:div {:class ["w-full" (when checkbox-disabled? "text-slate-400")]}
       [:label {:for   (when-not checkbox-disabled? recipe-id)
                :class ["flex flex-row" (when-not checkbox-disabled? "cursor-pointer")]}
        [:main {:class "grow"}
         [:h2 {:class "text-sm font-semibold mb-1"}
          name]
         [:p description]]

        (when (or (not deployable?) created?)
          [:aside {:class "ml-5 basis-2/5 items-center grow-0 shrink-0 flex"}
           [:> AlertTriangle {:class "mt-0 mr-4 grow-0 shrink-0 text-kleene-dark-text dark:text-white"}]
           [:span {:class "text-kleene-dark-text dark:text-white"}
            (cond
              (not deployable?)
              [:<>
               [:a {:on-click on-close :class "underline"} "Add extracts"]
               " with the following reports to unlock this recipe: " (clojure.string/join ", " report-types)]

              created?
              "This recipe has already been deployed."

              :else
              "This recipe can not be deployed.")]])

        (when more-info-button-enabled?
          [:div {:class ["h-12 w-12 text-center cursor-pointer rounded-full bg-kleene-neutral-05"
                         "bg-kleene-neutral-05 hover:bg-kleene-neutral-02 dark:bg-kleene-dark-02 dark:hover:bg-kleene-dark-05"]
                 :title (if open?
                          "Collapse details"
                          "Show details")
                 :on-click (fn [evt]
                             (.preventDefault evt)
                             (.stopPropagation evt)
                             (when (and (not open?) (nil? dag))
                               (fetch-details))
                             (toggle-open))}
           [:> (if open? ChevronUp ChevronDown) {:class "h-5 mt-4 grow-0 shrink-0 text-kleene-dark-text dark:text-white"}]])]]]

     (when open?
       [:div {:class "w-auto min-h-28 h-auto mt-4 mr-0 mb-2 rounded-xs border border-slate-300 dark:border-kleene-border"}
        (if (or details-loading? (nil? dag))
          [:div {:class "w-full h-28 flex items-center justify-center"}
           [c.spinner/inline-configurable {:color "primary" :size 40}]]

          [:div {:style {:height (str (:height dag) "px")}}
           [:> ReactFlow {:nodes (:nodes dag)
                          :edges (:edges dag)
                          :zoom-on-scroll false
                          :pan-on-drag true
                          :nodes-draggable false
                          :nodes-connectable false
                          :elements-selectable false
                          :fit-view true
                          :fit-view-options #js {:padding 0.2}
                          :nodeTypes node-types
                          :attributionPosition "bottom-right"}]])])]))

(defn deploy-recipes-dialog-render
  [{:keys [source]}]
  (let [source-type      (:source/source-type source)
        groups            (re-posh/subscribe [:recipes.subs/group-options source-type])
        recipes           (re-posh/subscribe [:recipes.subs/recipes-for-type source-type])
        {:keys [matched]} @(re-posh/subscribe [:transformations.subs/search-results])
        connectors        @(re-posh/subscribe [:connections.subs/all-enabled-connectors])]

    (when (empty? @groups) (dispatch [:group.events/search-groups]))
    (when (seq @recipes) (load-recipes-transforms @recipes))
    (dispatch [:recipes.api/fetch-recipes])

    (fn [{:keys [source on-close]}]
      (let [source-type     (:source/source-type source)

            version         (get-in source [:source/source :version])

            connector       (app.utils/find-connector-by-type-and-version connectors source-type version)

            searching?      @(re-posh/subscribe [:mold-http/loading? :transformations.api/load-search-results])

            group-options   (mapv (fn [{:group/keys [id name]}]
                                    {:value id :text name})
                                  @groups)

            fields         (concat
                            [{:name :group :value nil :process (fn [e] e)}]
                            (mapv (fn [r]
                                    (-> {:name    (:recipe/recipe-id r)
                                         :value   false
                                         :process (fn [e] e)}))
                                  @recipes))]
        [:<>
         [:div {:class "pb-4"}
          [ui.typography/h2 {:class "flex flex-col items-start gap-4 mt-2"}
           (str  (:name  connector) " Recipes")]]

         [form-component/form
          (->form-model
           {:id        :recipes-form
            :on-submit submit-recipes-form
            :fields    fields})
          (fn [fields errors on-submit]
            (let [selected-recipes (->> (dissoc fields :group) vals (filter :value) count)
                  group-id         (->> fields :group :value)
                  disabled-submit? (or (zero? selected-recipes) (empty? group-id))]
              [:<>
               [:div {:class "text-kleene-dark-text dark:text-white"}
                [ui.typography/h3 "1. Chose which recipes to deploy"]
                (for [recipe @recipes]
                  ^{:key (:recipe/recipe-id recipe)}
                  [recipe-checkbox {:recipe     recipe
                                    :fields     fields
                                    :errors     errors
                                    :search     matched
                                    :searching? searching?
                                    :on-close   on-close
                                    :source     source}])]

               [:div {:class "justify-between py-4 border-t border-t-slate-300 dark:border-kleene-border flex flex-row"}

                [:div {:class "flex items-center"}
                 [ui.typography/h3
                  {:class "mr-4"}
                  "2. Select which Transform Group to deploy them to:"]

                 [ui.fields/select
                  {:field        (get fields :group)
                   :placeholder  "Select group..."
                   :errors       errors
                   :id           :group
                   :required     true
                   :options      group-options
                   :native       false
                   :class        "w-72"}]]


                [:div {:class "flex items-center gap-2"}
                 [ui.button/button
                  {:variant (if disabled-submit? :secondary :primary)
                   :disabled disabled-submit?
                   :on-click on-submit}
                  "Create transforms"]]]]))]]))))

(defn deploy-recipes-dialog []
  (let [route     @(re-posh/subscribe [:app-route/pull])
        source-id (get-in route [:app-route/params :source-id])
        source    (when source-id
                    @(re-posh/subscribe [:source/pull [:source/source-id source-id]]))
        open?     (boolean (get-in route [:app-route/query :recipes]))
        on-close  (fn []
                    (dispatch [:app.events/navigate-query {:recipes nil}])
                    (reset! open-details-registry {}))]

    [ui.dialog/dialog {:open           open?
                       :on-open-change on-close
                       :content-props {:size :large}
                       :content (when source
                                  [deploy-recipes-dialog-render {:source   source
                                                                 :on-close on-close}])}]))
